import { uniq } from "lodash";
import type { VariableDB } from "@hightouch/lib/customer-data/decision-engine/types";
import {
  SYNC_CONCURRENCY_HELP,
  type DecisionEngineDestinationDefinition,
  type SyncSpec,
} from "./definition";
import {
  getMappingForVariable,
  hightouchPayloadMapping,
} from "./sync-config-helpers";

/**
 * Customer-facing contract: customers build their IAM segments on exactly this
 * tag key, so renaming it breaks every configured IAM. One shared key (rather
 * than per-flow/channel keys) is deliberate — a user is enrolled in at most
 * one AID in-app message at a time, so a repoint overwrites rather than
 * accumulates.
 */
export const ONE_SIGNAL_IAM_ENROLLMENT_TAG = "aid_iam_campaign";

/**
 */
export const ONE_SIGNAL_IAM_VARIABLE_TAG_PREFIX = "aid_var_";

/**
 * OneSignal tag keys must stay within [a-z0-9_]: dotted keys break IAM tag
 * substitution and other characters are undocumented territory.
 */
export function toOneSignalVariableTagKey(variableName: string): string {
  const sanitized = variableName.toLowerCase().replace(/[^a-z0-9_]/g, "_");
  return `${ONE_SIGNAL_IAM_VARIABLE_TAG_PREFIX}${sanitized}`;
}

/**
 * Sanitization is many-to-one ("Discount.Code" and "Discount Code" both map
 * to aid_var_discount_code), and colliding mappings would silently overwrite
 * one variable's value with the other's. Config generation runs at enablement
 * time, so throwing surfaces the conflict to the user instead.
 */
function buildVariableTagMappings(messageVariables: VariableDB[]) {
  const byTagKey = new Map<string, string>();
  for (const name of uniq(messageVariables.map((variable) => variable.name))) {
    const tagKey = toOneSignalVariableTagKey(name);
    const existing = byTagKey.get(tagKey);
    if (existing !== undefined) {
      throw new Error(
        `OneSignal in-app variables "${existing}" and "${name}" both map to tag "${tagKey}"; rename one so the tag keys are distinct`,
      );
    }
    byTagKey.set(tagKey, name);
  }
  return [...byTagKey.entries()].map(([tagKey, name]) =>
    getMappingForVariable(name, tagKey),
  );
}

/**
 * OneSignal has no per-user API to trigger an in-app message, so the inapp
 * channel enrolls users into a dashboard-built IAM by writing tags through
 * the users object sync. Constraints shaping this config:
 */
export function generateOneSignalInAppSyncConfig({
  messageVariables = [],
}: Pick<SyncSpec, "messageVariables">) {
  return {
    type: "object" as const,
    object: "users" as const,
    mode: "update" as const,
    externalIdMapping: { from: "external_id", to: "external_id" },
    deleteMode: "clear" as const,
    mappings: [
      {
        type: "standard" as const,
        from: "campaign_id",
        to: ONE_SIGNAL_IAM_ENROLLMENT_TAG,
      },
      ...buildVariableTagMappings(messageVariables),
    ],
  };
}

export const oneSignalDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "onesignal",
  // Stays "trigger" even though inapp emits a batch-shaped config: nothing
  // dispatches on category at runtime, and sms/push remain trigger sends.
  category: "trigger",
  // In-app enrolls users into a dashboard-built IAM via persistent tags, so
  // the sync model must keep one row per user (latest interaction wins)
  // instead of time-windowed per-send rows — otherwise clear-mode un-enrolls
  // everyone as rows age out of the send window.
  enrollmentChannelTypes: ["inapp"],
  generateSyncConfig: ({
    producer,
    channelType,
    messageTags,
    messageVariables,
  }) => {
    if (channelType === "inapp") {
      return generateOneSignalInAppSyncConfig({ messageVariables });
    }
    return {
      type: "trigger",
      templateId: { type: "standard", from: "campaign_id" },
      externalIdMapping: {
        from: "external_id",
        to: "external_id",
      },
      mappings: [hightouchPayloadMapping({ producer, messageTags })],
      targetChannel: channelType === "push" ? "push" : "sms",
    };
  },
  // Only the in-app enrollment sync (users object) reads a batch-size key;
  // 100 mirrors the rETL form max. Trigger sends (push/sms) hardcode both
  // batch size and parallelization, and the in-app `mode: "update"` is
  // load-bearing — so nothing else is tunable.
  syncTuning: ({ channelType }) =>
    channelType === "inapp"
      ? {
          batchSize: {
            configKey: "rowsPerBatch" as const,
            max: 100,
            label: "Concurrent profile operations",
            description:
              "Profiles processed together for in-app enrollment. Each lookup or update request addresses one profile.",
            help: SYNC_CONCURRENCY_HELP,
          },
        }
      : undefined,
};
